// assetViewMode — how the content browser lays its assets out: large icons,
// small icons, or a details list.
//
// The three modes and their metrics are PlayCanvas's, because the request was
// explicitly to match it (editor/src/editor/assets/asset-panel.ts +
// sass/editor/_editor-assets-panel.scss):
//
//   large ('lgrid', the default) — 104px cell, 64px thumb
//   small ('sgrid')              — 64×68 cell, 40px thumb
//   details                      — one row per asset, name column + meta
//
// The numbers live here rather than in the component so the layout is one
// lookup rather than three branches sprinkled through JSX, and so a mode can be
// added without touching the grid's render at all.
//
// Persisted per browser: a view mode is a workspace preference, like the panel
// sizes in shellLayout (which this mirrors — module singleton + subscribe, no
// imports, unit-testable).

const LS_KEY = 'aos-asset-view-mode-v1'

export const VIEW_MODES = Object.freeze(['large', 'small', 'details'])

/**
 * Per-mode layout metrics. `cell` is the grid's minimum column width — the grid
 * uses auto-fill so a wide dock packs more columns rather than stretching cells
 * past their intended size.
 */
export const VIEW_METRICS = Object.freeze({
  large: Object.freeze({
    label: 'Large icons', glyph: '▦',
    cell: 104, thumb: 64, gap: 10, pad: 10, fontSize: 11, showMeta: true,
  }),
  small: Object.freeze({
    label: 'Small icons', glyph: '▪',
    cell: 64, thumb: 40, gap: 6, pad: 5, fontSize: 9.5, showMeta: false,
  }),
  details: Object.freeze({
    label: 'Details', glyph: '☰',
    // A details row is full width; `thumb` is the leading icon.
    cell: null, thumb: 20, gap: 0, pad: 4, fontSize: 11, showMeta: true,
  }),
})

const DEFAULT_MODE = 'large'

export function sanitize(candidate) {
  return VIEW_MODES.includes(candidate) ? candidate : DEFAULT_MODE
}

/** The metrics for a mode, falling back to the default for an unknown one. */
export function metricsFor(mode) {
  return VIEW_METRICS[sanitize(mode)]
}

function load() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    return sanitize(raw)
  } catch {
    return DEFAULT_MODE
  }
}

let mode = load()
const listeners = new Set()

export const assetViewMode = {
  get() { return mode },

  set(next) {
    const clean = sanitize(next)
    if (clean === mode) return
    mode = clean
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, mode)
    } catch { /* private mode — the preference just won't survive the session */ }
    for (const fn of listeners) fn(mode)
  },

  metrics() { return metricsFor(mode) },

  subscribe(fn) {
    listeners.add(fn)
    fn(mode)
    return () => listeners.delete(fn)
  },

  /** Test seam. */
  _reset() {
    mode = DEFAULT_MODE
    listeners.clear()
  },
}

export default assetViewMode
